import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { get, put, del } from '../utils/api';
import {
  normalizeSlotsToArray,
  getMixedSlotDisplayValue,
  isSlotCorrectInContext,
  isBlankBoxCorrectInContext,
} from '../utils/quizResultsUtils';
import ResultOverlayBox from './ResultOverlayBox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs';
import { Button } from '@/components/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card';
import Quiz from '../views/Quiz/Quiz';
import LessonRecap from './Sparks/LessonRecap';

// Modal component for viewing completed work
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50" onClick={handleOutsideClick}>
      <div className="bg-white p-6 rounded-lg max-w-6xl w-full max-h-[80vh] overflow-y-auto max-md:mx-4 max-md:max-h-[85dvh] max-md:p-4">
        <Button onClick={onClose} className="mb-4">Close</Button>
        {children}
      </div>
    </div>
  );
};

function Homework({isCoreSkills = false}) {
  const [pendingWork, setPendingWork] = useState([]);
  const [completedWork, setCompletedWork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [showRecap, setShowRecap] = useState(false);
  const { loggedInChild } = useAuth();
  const navigate = useNavigate();

  const getTipsProgress = (submission) => {
    const total = submission?.tipsTotal ?? submission?.questionAttempts?.length ?? 0;
    const readyFromTips = Array.isArray(submission?.parentTips)
      ? submission.parentTips.filter((tip) => (tip?.shortTip || '').trim() || (tip?.modelAnswer || '').trim()).length
      : 0;
    const ready = typeof submission?.tipsReady === 'number' ? submission.tipsReady : readyFromTips;
    const failed = typeof submission?.tipsFailed === 'number' ? submission.tipsFailed : 0;
    const percent = total > 0 ? Math.min(100, Math.round((ready / total) * 100)) : 0;
    return { total, ready, failed, percent };
  };

  const shouldPollTips = (submission) => {
    if (!submission) return false;
    const { total, ready } = getTipsProgress(submission);
    const status = submission?.tipsStatus;
    if (['pending', 'partial', 'failed', 'not_started'].includes(status)) return true;
    return total > 0 && ready < total;
  };

  const getTipsStatusBanner = (submission) => {
    const status = submission?.tipsStatus;
    const { total, ready, failed } = getTipsProgress(submission);

    if (status === 'pending') {
      return {
        className: 'mb-6 p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900',
        text: `Parent tips are generating (${ready}/${total} ready). This updates automatically while you keep this window open.`,
      };
    }

    if (status === 'partial') {
      return {
        className: 'mb-6 p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900',
        text: `Parent tips are still being retried (${ready}/${total} ready, ${failed} failed). If it stalls for over a minute, close and click View Results again.`,
      };
    }

    if (status === 'failed') {
      return {
        className: 'mb-6 p-4 rounded-lg border border-red-200 bg-red-50 text-red-800',
        text: 'Parent tips are retrying in the background. If this does not improve shortly, close and click View Results again.',
      };
    }

    return null;
  };

  useEffect(() => {
    if (loggedInChild?._id) {
      fetchPendingWork();
    }
  }, [loggedInChild]);

  useEffect(() => {
    if (activeTab === 'completed' && loggedInChild?._id && completedWork.length === 0) {
      fetchCompletedWork();
    }
  }, [activeTab, loggedInChild]);

  useEffect(() => {
    if (!isModalOpen || !selectedSubmission?._id || !shouldPollTips(selectedSubmission)) {
      return;
    }

    let cancelled = false;
    let inFlight = false;

    const pollSubmission = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const refreshedSubmission = await get(`/quiz/quiz-submissions/admin/${selectedSubmission._id}?ensureParentTips=true`);
        if (!cancelled) {
          setSelectedSubmission(refreshedSubmission);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error polling submission tips progress:', error);
        }
      } finally {
        inFlight = false;
      }
    };

    const intervalId = setInterval(pollSubmission, 4000);
    pollSubmission();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isModalOpen, selectedSubmission?._id, selectedSubmission?.tipsStatus, selectedSubmission?.tipsReady, selectedSubmission?.tipsTotal, selectedSubmission?.tipsFailed]);

  const fetchPendingWork = async () => {
    try {
      setLoading(true);
      const response = await get(`/quiz/pending-work/${loggedInChild._id}?isCoreSkills=${isCoreSkills}`);
      setPendingWork(response);
    } catch (error) {
      console.error('Error fetching pending work:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedWork = async () => {
    try {
      setCompletedLoading(true);
      const response = await get(`/quiz/completed-work/${loggedInChild._id}?isCoreSkills=${isCoreSkills}`);
      setCompletedWork(response);
    } catch (error) {
      console.error('Error fetching completed work:', error);
    } finally {
      setCompletedLoading(false);
    }
  };

  const handleStartWork = (workItem) => {
    if (!workItem.canStart) {
      return; // Don't allow starting if not enabled
    }

    // Set active quiz to embed it in this component
    setActiveQuiz(workItem);
  };

  const handleQuizComplete = async (completedQuizSubmission) => {
    // Show the recap, then return to the assignments view
    setShowRecap(true);
    setActiveQuiz(null);
    
    // Handle lesson progression for regular quizzes (not homework)
    if (completedQuizSubmission && !completedQuizSubmission.isHomework) {
      try {
        // Get the childTopic document for this child and topic
        const submissionTrack = completedQuizSubmission.track;
        const trackQuery = submissionTrack ? `?track=${submissionTrack}` : '';
        const childTopicResponse = await get(`/childTopics/${loggedInChild._id}/${completedQuizSubmission.topicId}${trackQuery}`, true);

        const activeStage = childTopicResponse?.learningStage;

        // Check if this childTopic matches our criteria
        if (childTopicResponse &&
            childTopicResponse.currentLesson._id === completedQuizSubmission.lessonId &&
            activeStage === 'quiz') {

          // Get the full topic document to access ordered lessons
          const topicResponse = await get(`/topics/${completedQuizSubmission.topicId}`);
          const currentLessonId = childTopicResponse.currentLesson._id;

          // Find current lesson's position in the ordered lessons array
          const currentLessonIndex = topicResponse.lessons.findIndex(
            lesson => lesson.lessonId._id === currentLessonId
          );

          // Check if there's a next lesson
          if (currentLessonIndex !== -1 && currentLessonIndex < topicResponse.lessons.length - 1) {
            // Get the next lesson
            const nextLesson = topicResponse.lessons[currentLessonIndex + 1].lessonId;

            // Update childTopic with next lesson
            await put(`/childTopics/${loggedInChild._id}/${completedQuizSubmission.topicId}`, {
              currentLesson: nextLesson._id,
              currentLessonStep: 0,
              learningStage: 'lesson',
              track: submissionTrack,
            }, true);
            
            console.log('Advanced to next lesson:', nextLesson.title);
          } else if (currentLessonIndex === topicResponse.lessons.length - 1) {

            if (isCoreSkills) {
              // This was the last lesson in the topic
              // Clear the current topic from the child
              await put(`/children/${loggedInChild._id}/current-home-topic`, { topicId: null }, true);
              
              // Delete the childTopic entry
              await del(`/childTopics/${loggedInChild._id}/${completedQuizSubmission.topicId}?track=${submissionTrack}`, true);
              
              console.log('Completed entire topic');
            } else {
              markCoreSkillCompleted();
            }
          }
        }
      } catch (error) {
        console.error('Error handling lesson progression:', error);
        // Don't break the UI if progression fails
      }
    }
    
    // Refresh pending work to reflect completion
    fetchPendingWork();
    
    // Always refresh completed work when a quiz is completed
    fetchCompletedWork();
  };

  const handleViewCompleted = async (workItem) => {
    setIsModalOpen(true);
    setIsReportLoading(true);
    setSelectedSubmission(null);
    try {
      const submissionDetails = await get(`/quiz/quiz-submissions/admin/${workItem.submissionId}?ensureParentTips=true`);
      setSelectedSubmission(submissionDetails);
    } catch (error) {
      console.error('Error fetching submission details:', error);
    } finally {
      setIsReportLoading(false);
    }
  };


  const markCoreSkillCompleted = async () => {
        try {
                  await put(`/children/${loggedInChild._id}/complete-current-core-skill`, {}, true);
              } catch (error) {
                  console.error('Error marking core skill as completed:', error);
              }
      }

  // Function to get display-friendly answer text
  const getDisplayedAnswer = (attempt) => {
    if (!attempt.LessonQuestionId) {
      return 'Question data not available';
    }

    const questionType = attempt.LessonQuestionId.questionType;
    
    if (questionType === 'Free-Text-Question') {
      return attempt.chosenAnswer;
    } else if (questionType === 'Multiple-Choice-Question' || questionType === 'Drop-Down-List') {
      if (!attempt.LessonQuestionId.answers) {
        return 'Answer options not available';
      }
      const chosenAnswerObj = attempt.LessonQuestionId.answers.find(
        answer => answer.id === attempt.chosenAnswer
      );
      return chosenAnswerObj ? chosenAnswerObj.text : 'Answer not found';
    } else if (questionType === 'Blank-Box-Question' && attempt.BoxTypeAnswers) {
      const allBoxes = attempt.LessonQuestionId.blankBoxes ?? [];
      return Object.entries(attempt.BoxTypeAnswers).map(([boxId, answer], index) => {
        const box = allBoxes.find(b => b.id === boxId);
        const isCorrect = !!box && isBlankBoxCorrectInContext(box, allBoxes, attempt.BoxTypeAnswers);
        return `Box ${index + 1}: ${answer}${box && !isCorrect ? ` (Correct: ${box.answer})` : ''}`;
      }).join('; ');
    } else if (questionType === 'Drag-And-Drop-Question' && attempt.BoxTypeAnswers) {
      return Object.entries(attempt.BoxTypeAnswers).map(([boxId, label], index) => {
        const box = attempt.LessonQuestionId.dragBoxes?.find(b => b.id === boxId);
        return `Box ${index + 1}: ${label}${box && label !== box.label ? ` (Correct: ${box.label})` : ''}`;
      }).join('; ');
    } else if (questionType === 'Mixed' && attempt.BoxTypeAnswers) {
      const slots = normalizeSlotsToArray(attempt.LessonQuestionId.slots);
      return slots.map((slot, index) => {
        const value = attempt.BoxTypeAnswers[slot.id] ?? '';
        const display = getMixedSlotDisplayValue(slot, value);
        const isCorrect = isSlotCorrectInContext(slot, slots, attempt.BoxTypeAnswers);
        return `Slot ${index + 1}: ${display} (${isCorrect ? 'Correct' : 'Incorrect'})`;
      }).join('; ');
    } else {
      return 'Answer format not supported';
    }
  };

  // Function to get display-friendly answer text for explanation questions
  const getExplanationDisplayedAnswer = (attempt) => {
    if (!attempt.explanationQuestionId || !attempt.explanationChosenAnswer) {
      return 'No answer provided';
    }

    // If explanation question data is not properly populated
    if (typeof attempt.explanationQuestionId !== 'object') {
      return attempt.explanationChosenAnswer;
    }

    const questionType = attempt.explanationQuestionId.questionType;
    
    if (questionType === 'Free-Text-Question') {
      return attempt.explanationChosenAnswer;
    } else if (questionType === 'Multiple-Choice-Question' || questionType === 'Drop-Down-List') {
      if (!attempt.explanationQuestionId.answers) {
        return attempt.explanationChosenAnswer; // Fallback to raw answer if answers not available
      }
      const chosenAnswerObj = attempt.explanationQuestionId.answers.find(
        answer => answer.id === attempt.explanationChosenAnswer
      );
      return chosenAnswerObj ? chosenAnswerObj.text : attempt.explanationChosenAnswer;
    } else {
      return attempt.explanationChosenAnswer;
    }
  };

  const renderPendingWork = () => {
    if (!loggedInChild?._id) {
      return (
        <div className="text-center py-8">
          <Card className="max-w-md mx-auto border-blue-200 bg-blue-50">
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-blue-800 font-medium">
                  Please log in to your child account in the <strong>Children Dashboard</strong> to view their assignments.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">Loading pending work...</p>
        </div>
      );
    }

    if (pendingWork.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">No pending work found.</p>
        </div>
      );
    }

    // Group pending work by lesson
    const groupedWork = pendingWork.reduce((groups, workItem) => {
      const lessonId = workItem.lessonId;
      if (!groups[lessonId]) {
        groups[lessonId] = {
          lessonTitle: workItem.lessonTitle,
          quiz: null,
          homework: null
        };
      }
      
      if (workItem.isHomework) {
        groups[lessonId].homework = workItem;
      } else {
        groups[lessonId].quiz = workItem;
      }
      
      return groups;
    }, {});

    return (
      <div className="space-y-6">
        {Object.values(groupedWork).map((lessonGroup, index) => (
          <div key={index} className="border-2 border-black rounded-lg p-6 bg-white">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {lessonGroup.lessonTitle}
            </h3>
            
            <div className="space-y-4">
              {/* Lesson Quiz */}
              {lessonGroup.quiz && (
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-blue-900">Lesson Quiz</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Complete the main lesson practice questions
                    </p>
                  </div>
                  <div className="ml-4">
                    <Button
                      onClick={() => handleStartWork(lessonGroup.quiz)}
                      disabled={!lessonGroup.quiz.canStart}
                      className={
                        lessonGroup.quiz.canStart
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }
                    >
                      {lessonGroup.quiz.buttonText}
                    </Button>
                  </div>
                </div>
              )}

              {/* Homework */}
              {lessonGroup.homework && (
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-purple-900">Homework Assignment</p>
                    <p className="text-sm text-purple-700 mt-1">
                      Additional practice questions for this lesson
                    </p>
                    {!lessonGroup.homework.canStart && (
                      <p className="text-sm text-orange-600 mt-1">
                        Complete the lesson quiz first to unlock homework
                      </p>
                    )}
                  </div>
                  <div className="ml-4">
                    <Button
                      onClick={() => handleStartWork(lessonGroup.homework)}
                      disabled={!lessonGroup.homework.canStart}
                      className={
                        lessonGroup.homework.canStart
                          ? 'bg-purple-600 hover:bg-purple-700 text-white'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }
                    >
                      {lessonGroup.homework.buttonText}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCompletedWork = () => {
    if (!loggedInChild?._id) {
      return (
        <div className="text-center py-8">
          <Card className="max-w-md mx-auto border-blue-200 bg-blue-50">
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-blue-800 font-medium">
                  Please log in to your child account in the <strong>Children Dashboard</strong> to view their assignments.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (completedLoading) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">Loading completed work...</p>
        </div>
      );
    }

    if (completedWork.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">No completed work found.</p>
        </div>
      );
    }

    // Group completed work by lesson
    const groupedCompletedWork = completedWork.reduce((groups, workItem) => {
      const lessonId = workItem.lessonId;
      if (!groups[lessonId]) {
        groups[lessonId] = {
          lessonTitle: workItem.lessonTitle,
          assignments: []
        };
      }
      groups[lessonId].assignments.push(workItem);
      return groups;
    }, {});

    // Sort assignments within each lesson group chronologically (most recent first)
    Object.values(groupedCompletedWork).forEach(lessonGroup => {
      lessonGroup.assignments.sort((a, b) => {
        const dateA = new Date(a.completionDate || a.createdAt);
        const dateB = new Date(b.completionDate || b.createdAt);
        return dateB - dateA; // Most recent first
      });
    });

    // Sort lesson groups by most recent completion date within each group
    const sortedLessonGroups = Object.values(groupedCompletedWork).sort((a, b) => {
      const mostRecentA = new Date(a.assignments[0].completionDate || a.assignments[0].createdAt);
      const mostRecentB = new Date(b.assignments[0].completionDate || b.assignments[0].createdAt);
      return mostRecentB - mostRecentA; // Most recent lessons first
    });

    return (
      <div className="space-y-6">
        {sortedLessonGroups.map((lessonGroup, groupIndex) => (
          <div key={groupIndex} className="border-2 border-black rounded-lg p-6 bg-white">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {lessonGroup.lessonTitle}
            </h3>
            
            <div className="space-y-4">
              {lessonGroup.assignments.map((workItem) => (
                <Card key={workItem.submissionId} className="border border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-1">
                          {workItem.type === 'homework' ? 'Homework Assignment' : 'Lesson Quiz'}
                          {workItem.attemptNumber && (
                            <span className="ml-2 text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                              Attempt #{workItem.attemptNumber}
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-green-600 mt-1">
                          Score: {workItem.correctCount}/{workItem.totalCount} ({workItem.score}%)
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Completed: {workItem.completionDate ? new Date(workItem.completionDate).toLocaleString() : '-'}
                        </p>
                      </div>
                      <div className="ml-4">
                        <Button
                          onClick={() => handleViewCompleted(workItem)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {workItem.buttonText}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 max-md:p-2">
      <div className="max-w-7xl mx-auto">
        {showRecap ? (
          <LessonRecap continueLabel="Back to Assignments" onContinue={() => setShowRecap(false)} showShop>
            You&apos;ve finished the assignment!
          </LessonRecap>
        ) : activeQuiz ? (
          // Show embedded quiz
          <div>
            <div className="mb-4">
              <Button 
                onClick={() => setActiveQuiz(null)}
                className="bg-gray-600 hover:bg-gray-700 text-white"
              >
                ← Back to Assignments
              </Button>
            </div>
            <Quiz
              quizSubmissionId={activeQuiz.submissionId}
              lessonId={activeQuiz.lessonId}
              topicId={activeQuiz.topicId}
              preventNavigation={true}
              onQuizComplete={handleQuizComplete}
            />
          </div>
        ) : (
          // Show assignments tabs
          <>
            <h1 className="text-3xl font-bold text-gray-900 mb-6">
              {loggedInChild?.name ? `${loggedInChild.name}'s Assignments` : 'Assignments'}
            </h1>
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="pending">Pending</TabsTrigger>
                <TabsTrigger value="completed">Completed</TabsTrigger>
              </TabsList>
              
              <TabsContent value="pending" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Pending Work</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {renderPendingWork()}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="completed" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Completed Work</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {renderCompletedWork()}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* Completed Work Details Modal */}
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          {isReportLoading ? (
            <div className="py-12 text-center text-gray-600">
              <p className="text-lg">Loading your report…</p>
            </div>
          ) : selectedSubmission ? (
            <div>
              {(() => {
                const banner = getTipsStatusBanner(selectedSubmission);
                const { ready, total, percent, failed } = getTipsProgress(selectedSubmission);
                if (!banner) return null;
                return (
                  <div className={banner.className}>
                    <p className="text-sm font-medium">{banner.text}</p>
                    <div className="mt-3">
                      <div className="w-full h-2 rounded-full bg-white/60 overflow-hidden">
                        <div className="h-full rounded-full bg-[#5C3BD1] transition-all duration-500" style={{ width: `${percent}%` }} />
                      </div>
                      <p className="text-xs mt-2">
                        {ready}/{total} tips generated{failed > 0 ? `, ${failed} failed` : ''}.
                      </p>
                    </div>
                  </div>
                );
              })()}
                             <h2 className="text-2xl font-bold mb-4">
                 {selectedSubmission.lessonId?.title || 'Unknown Lesson'}
                 <span className={`ml-2 px-2 py-1 text-sm rounded-full ${
                   selectedSubmission.isHomework 
                     ? 'bg-purple-100 text-purple-800' 
                     : 'bg-blue-100 text-blue-800'
                 }`}>
                   {selectedSubmission.isHomework ? 'Homework Quiz' : 'Lesson Quiz'}
                 </span>
               </h2>
              
              {/* Score Summary */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-blue-800">
                    Final Score
                  </h3>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-900">
                      {selectedSubmission.questionAttempts.filter(q => q.QuestionStatus === 'correct').length}/
                      {selectedSubmission.questionAttempts.length}
                    </div>
                    <div className="text-sm text-blue-700">
                      ({Math.round((selectedSubmission.questionAttempts.filter(q => q.QuestionStatus === 'correct').length / selectedSubmission.questionAttempts.length) * 100)}%)
                    </div>
                  </div>
                </div>
                <div className="text-sm text-blue-600 mt-2">
                  Completed: {(selectedSubmission.completionDate || selectedSubmission.createdAt) 
                    ? new Date(selectedSubmission.completionDate || selectedSubmission.createdAt).toLocaleString() 
                    : '-'}
                </div>
              </div>

              {/* Questions */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Question Details</h3>
                
                {selectedSubmission.questionAttempts.map((attempt, index) => (
                  <div key={index} className="mb-10">
                    <div className="flex gap-8 max-md:flex-col max-md:gap-4">
                      {/* Original Question Column */}
                      <div className={`flex-1 p-6 max-md:p-4 border rounded ${
                        attempt.QuestionStatus === 'correct' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <p className="font-bold mb-2">Question {index + 1}:</p>
                        {attempt.LessonQuestionId ? (
                          <>
                            <p className="mb-2"><strong>Question:</strong> {attempt.LessonQuestionId.question}</p>
                            
                            {/* Display the question image if it exists */}
                            {attempt.LessonQuestionId.image && (
                              <div className="mb-3">
                                <p className="font-medium mb-1">Question Image:</p>
                                <div className="relative inline-block" style={{ maxWidth: '100%' }}>
                                  <img 
                                    src={attempt.LessonQuestionId.image} 
                                    alt="Question" 
                                    className="max-h-[200px] max-w-full object-contain border border-gray-300 rounded p-1"
                                  />
                                  
                                  {/* Blank box / drag-and-drop / Mixed: overlay student answers in boxes */}
                                  {attempt.LessonQuestionId.questionType === 'Blank-Box-Question' &&
                                    attempt.LessonQuestionId.blankBoxes &&
                                    attempt.BoxTypeAnswers &&
                                    attempt.LessonQuestionId.blankBoxes.map((box) => {
                                      const studentAnswer = attempt.BoxTypeAnswers?.[box.id] ?? '';
                                      const isCorrect = !!studentAnswer && isBlankBoxCorrectInContext(box, attempt.LessonQuestionId.blankBoxes, attempt.BoxTypeAnswers);
                                      return (
                                        <ResultOverlayBox key={box.id} left={box.x} top={box.y} width={box.width} height={box.height} isCorrect={isCorrect}>
                                          {studentAnswer}
                                        </ResultOverlayBox>
                                      );
                                    })}
                                  {attempt.LessonQuestionId.questionType === 'Drag-And-Drop-Question' &&
                                    attempt.LessonQuestionId.dragBoxes &&
                                    attempt.BoxTypeAnswers &&
                                    attempt.LessonQuestionId.dragBoxes.map((box) => {
                                      const studentAnswer = attempt.BoxTypeAnswers?.[box.id] ?? '';
                                      const isCorrect = studentAnswer && box.label && studentAnswer === box.label;
                                      return (
                                        <ResultOverlayBox key={box.id} left={box.x} top={box.y} width={box.width} height={box.height} isCorrect={isCorrect}>
                                          {studentAnswer}
                                        </ResultOverlayBox>
                                      );
                                    })}
                                  {attempt.LessonQuestionId.questionType === 'Mixed' &&
                                    attempt.LessonQuestionId.slots &&
                                    attempt.BoxTypeAnswers &&
                                    (() => {
                                      const slots = normalizeSlotsToArray(attempt.LessonQuestionId.slots);
                                      const withPosition = slots.filter((s) => s.x != null && s.y != null && s.width != null && s.height != null);
                                      return withPosition.map((slot) => {
                                        const value = attempt.BoxTypeAnswers[slot.id] ?? '';
                                        const display = getMixedSlotDisplayValue(slot, value);
                                        const isCorrect = isSlotCorrectInContext(slot, slots, attempt.BoxTypeAnswers);
                                        return (
                                          <ResultOverlayBox key={slot.id} left={slot.x} top={slot.y} width={slot.width} height={slot.height} isCorrect={isCorrect}>
                                            {display}
                                          </ResultOverlayBox>
                                        );
                                      });
                                    })()}
                                </div>
                              </div>
                            )}
                            
                            {!(attempt.LessonQuestionId.questionType === 'Blank-Box-Question' || attempt.LessonQuestionId.questionType === 'Drag-And-Drop-Question' || attempt.LessonQuestionId.questionType === 'Mixed') && (
                              <p className="mb-2"><strong>Student Answer:</strong> {getDisplayedAnswer(attempt)}</p>
                            )}
                            <p className="font-medium">
                              <strong>Result:</strong> {attempt.QuestionStatus === 'correct' ? 
                                <span className="text-green-600">Correct</span> : 
                                <span className="text-red-600">Incorrect</span>
                              }
                            </p>
                          </>
                        ) : (
                          <p className="mb-2 text-red-600">Question data not available (question may have been deleted)</p>
                        )}
                      </div>
                      
                      {/* Explanation Question Column - Only show if there's an explanation */}
                      {attempt.explanationQuestionId && (
                        <div className={`flex-1 p-6 max-md:p-4 border rounded ${
                          !attempt.explanationResponseStatus
                            ? 'bg-gray-100'
                            : attempt.explanationResponseStatus === 'correct'
                              ? 'bg-green-100'
                              : 'bg-red-100'
                        }`}>
                          <p className="font-bold mb-2">Explanation Question:</p>
                          
                          {typeof attempt.explanationQuestionId === 'object' && attempt.explanationQuestionId ? (
                            <p className="mb-2"><strong>Question:</strong> {attempt.explanationQuestionId.question}</p>
                          ) : (
                            <p className="mb-2 text-red-600">Explanation question data not available</p>
                          )}
                          
                          {typeof attempt.explanationQuestionId === 'object' && attempt.explanationQuestionId && attempt.explanationQuestionId.image && (
                            <div className="mb-3">
                              <p className="font-medium mb-1">Question Image:</p>
                              <div className="relative inline-block" style={{ maxWidth: '100%' }}>
                                <img 
                                  src={attempt.explanationQuestionId.image} 
                                  alt="Explanation Question" 
                                  className="max-h-[200px] max-w-full object-contain border border-gray-300 rounded p-1"
                                />
                                
                                {/* For blank box explanation questions, overlay student answers in boxes */}
                                {attempt.explanationQuestionId.questionType === 'Blank-Box-Question' &&
                                  attempt.explanationQuestionId.blankBoxes &&
                                  attempt.explanationBoxTypeAnswers &&
                                  attempt.explanationQuestionId.blankBoxes.map((box, boxIndex) => {
                                    const studentAnswer = attempt.explanationBoxTypeAnswers?.[box.id] ?? '';
                                    const isCorrect = !!studentAnswer && isBlankBoxCorrectInContext(box, attempt.explanationQuestionId.blankBoxes, attempt.explanationBoxTypeAnswers);
                                    return (
                                      <div 
                                        key={box.id}
                                        className={`absolute flex items-center justify-center select-none`}
                                        style={{
                                          left: `${box.x}%`,
                                          top: `${box.y}%`,
                                          width: `${box.width}%`,
                                          height: `${box.height}%`,
                                          backgroundColor: isCorrect ? 'rgba(200,255,200,0.7)' : 'rgba(255,200,200,0.7)',
                                          border: `2px solid ${isCorrect ? '#00b400' : '#b40000'}`,
                                          borderRadius: 4,
                                          fontWeight: 'bold',
                                          color: '#222',
                                          fontSize: '0.95em',
                                          textAlign: 'center',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          pointerEvents: 'none',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {studentAnswer}
                                      </div>
                                    );
                                  })}
                                
                                {/* For drag and drop explanation questions, overlay student answers in boxes */}
                                {attempt.explanationQuestionId.questionType === 'Drag-And-Drop-Question' && 
                                  attempt.explanationQuestionId.dragBoxes &&
                                  attempt.explanationBoxTypeAnswers &&
                                  attempt.explanationQuestionId.dragBoxes.map((box, boxIndex) => {
                                    const studentAnswer = attempt.explanationBoxTypeAnswers?.[box.id] ?? '';
                                    const isCorrect = studentAnswer && box.label && studentAnswer === box.label;
                                    return (
                                      <div 
                                        key={box.id}
                                        className={`absolute flex items-center justify-center select-none`}
                                        style={{
                                          left: `${box.x}%`,
                                          top: `${box.y}%`,
                                          width: `${box.width}%`,
                                          height: `${box.height}%`,
                                          backgroundColor: isCorrect ? 'rgba(200,255,200,0.7)' : 'rgba(255,200,200,0.7)',
                                          border: `2px solid ${isCorrect ? '#00b400' : '#b40000'}`,
                                          borderRadius: 4,
                                          fontWeight: 'bold',
                                          color: '#222',
                                          fontSize: '0.95em',
                                          textAlign: 'center',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          pointerEvents: 'none',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {studentAnswer}
                                      </div>
                                    );
                                  })}

                                {/* For mixed explanation questions, overlay student answers in each slot */}
                                {attempt.explanationQuestionId.questionType === 'Mixed' &&
                                  attempt.explanationQuestionId.slots &&
                                  attempt.explanationBoxTypeAnswers &&
                                  (() => {
                                    const slots = normalizeSlotsToArray(attempt.explanationQuestionId.slots);
                                    const withPosition = slots.filter((s) => s.x != null && s.y != null && s.width != null && s.height != null);
                                    return withPosition.map((slot) => {
                                      const value = attempt.explanationBoxTypeAnswers[slot.id] ?? '';
                                      const display = getMixedSlotDisplayValue(slot, value);
                                      const isCorrect = isSlotCorrectInContext(slot, slots, attempt.explanationBoxTypeAnswers);
                                      return (
                                        <div
                                          key={slot.id}
                                          className={`absolute flex items-center justify-center select-none`}
                                          style={{
                                            left: `${slot.x}%`,
                                            top: `${slot.y}%`,
                                            width: `${slot.width}%`,
                                            height: `${slot.height}%`,
                                            backgroundColor: isCorrect ? 'rgba(200,255,200,0.7)' : 'rgba(255,200,200,0.7)',
                                            border: `2px solid ${isCorrect ? '#00b400' : '#b40000'}`,
                                            borderRadius: 4,
                                            fontWeight: 'bold',
                                            color: '#222',
                                            fontSize: '0.95em',
                                            textAlign: 'center',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            pointerEvents: 'none',
                                            overflow: 'hidden',
                                          }}
                                        >
                                          {display}
                                        </div>
                                      );
                                    });
                                  })()}
                              </div>
                            </div>
                          )}
                          
                          {!attempt.explanationResponseStatus ? (
                            <p className="italic">Not attempted</p>
                          ) : (
                            <>
                              {!(attempt.explanationQuestionId?.questionType === 'Blank-Box-Question' || attempt.explanationQuestionId?.questionType === 'Drag-And-Drop-Question' || attempt.explanationQuestionId?.questionType === 'Mixed') && (
                                <p className="mb-2"><strong>Answer:</strong> {
                                  getExplanationDisplayedAnswer(attempt)
                                }</p>
                              )}
                              <p className="mt-2"><strong>Result:</strong> {
                                attempt.explanationResponseStatus === 'correct' ? 'Correct' : 'Incorrect'
                              }</p>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Parent tip / Model answer */}
                    {selectedSubmission.parentTips && selectedSubmission.parentTips[index] &&
                      (attempt.QuestionStatus === 'correct' ? selectedSubmission.parentTips[index].shortTip : selectedSubmission.parentTips[index].modelAnswer) && (
                      <div className="mt-3 p-4 rounded border bg-gray-50 border-gray-200 w-full">
                        <p className="font-medium text-gray-700 mb-1">
                          {attempt.QuestionStatus === 'correct' ? 'Spot on!' : 'Model answer'}
                        </p>
                        <p className="text-gray-800">
                          {attempt.QuestionStatus === 'correct'
                            ? selectedSubmission.parentTips[index].shortTip
                            : selectedSubmission.parentTips[index].modelAnswer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </Modal>
      </div>
    </div>
  );
}

export default Homework; 